// src/processors/style/style-content-processor.ts - Style Content Processing with SASS Support
import { XConVitePluginOptions } from '../../types';
import { Logger } from "../../utils/logger";

/**
 * Handles style content processing including SASS compilation, minification and escaping
 */
export class StyleContentProcessor {
    private sassCompiler: any = null;

    constructor(
        private options: Required<XConVitePluginOptions>,
        private logger: Logger,
        private terser?: any
    ) {
        this.initializeSassCompiler();
    }

    /**
     * Initialize SASS compiler if available
     */
    private initializeSassCompiler(): void {
        try {
            this.sassCompiler = require('sass');
            this.logger.debug('SASS compiler initialized');
        } catch (e) {
            this.logger.debug('SASS compiler not available - SCSS/SASS files will be processed as CSS');
        }
    }

    /**
     * Check if SASS compiler is available
     */
    private isSassAvailable(): boolean {
        return this.sassCompiler !== null;
    }

    /**
     * Process CSS content with SASS compilation, minification and escaping
     */
    processStyleContent(content: string, filePath: string): string {
        let processed = content;

        // Compile SASS/SCSS to CSS if needed
        if (this.needsSassCompilation(filePath)) {
            processed = this.compileSass(content, filePath);
        }

        if (this.options.minifyStyles) {
            processed = this.minifyCSS(processed);
            this.logger.debug('CSS minified');
        }

        // Always escape for JavaScript string
        return this.escapeForJavaScript(processed);
    }

    /**
     * Check if file needs SASS compilation
     */
    private needsSassCompilation(filePath: string): boolean {
        return filePath.endsWith('.scss') ||
            filePath.endsWith('.sass');
    }

    /**
     * Compile SASS/SCSS to CSS using SASS's built-in resolver
     */
    private compileSass(content: string, filePath: string): string {
        if (!this.isSassAvailable()) {
            this.logger.warn(`SASS compiler not available, processing ${filePath} as CSS`);
            return content;
        }

        try {
            const path = require('path');
            const isSass = filePath.endsWith('.sass');

            // Get the directory of the SCSS file for import resolution
            const fileDir = path.dirname(filePath);

            // Find node_modules from the file location
            const nodeModulesPath = this.findNodeModules(fileDir);

            // Build load paths for SASS resolver
            // SASS will automatically look for files in these directories
            const loadPaths = [
                fileDir,                      // Current file directory (for relative imports)
                nodeModulesPath,              // node_modules for package imports
                path.resolve(fileDir, '..'),  // Parent directory
                path.resolve(fileDir, '../..') // Grandparent directory
            ];

            this.logger.debug(`SASS compiling: ${path.basename(filePath)}`);
            this.logger.debug(`Load paths: ${loadPaths.join(', ')}`);

            // Let SASS handle all the import resolution with its built-in resolver
            const result = this.sassCompiler.compileString(content, {
                syntax: isSass ? 'indented' : 'scss',
                style: 'expanded',
                sourceMap: false,
                loadPaths: loadPaths
            });

            this.logger.debug(`✅ SASS compiled successfully: ${path.basename(filePath)}`);
            return result.css;
        } catch (error: any) {
            this.logger.error(`SASS compilation error in ${filePath}:`);
            this.logger.error(error.message);
            this.logger.warn(`Falling back to raw content for ${filePath}`);
            return content;
        }
    }

    /**
     * Find node_modules directory from a given path
     */
    private findNodeModules(startPath: string): string {
        const path = require('path');
        const fs = require('fs');

        let currentPath = startPath;

        // Go up maximum 10 levels to find node_modules
        for (let i = 0; i < 10; i++) {
            const nodeModulesPath = path.join(currentPath, 'node_modules');

            if (fs.existsSync(nodeModulesPath)) {
                return nodeModulesPath;
            }

            const parentPath = path.dirname(currentPath);

            // If we reached the root, stop
            if (parentPath === currentPath) {
                break;
            }

            currentPath = parentPath;
        }

        // Fallback to process.cwd() + node_modules
        return path.join(process.cwd(), 'node_modules');
    }

    /**
     * Minify CSS content using fallback method
     */
    private minifyCSS(content: string): string {
        return content
            .replace(/\/\*[\s\S]*?\*\//g, '') // Remove CSS comments
            .replace(/\r?\n/g, ' ')           // Remove line breaks
            .replace(/\s+/g, ' ')             // Collapse whitespace
            .replace(/;\s*}/g, '}')           // Remove semicolon before }
            .replace(/\s*{\s*/g, '{')         // Clean around {
            .replace(/;\s*/g, ';')            // Clean after ;
            .replace(/,\s*/g, ',')            // Clean after ,
            .replace(/:\s*/g, ':')            // Clean after :
            .replace(/\s*>\s*/g, '>')         // Clean around >
            .replace(/\s*\+\s*/g, '+')        // Clean around +
            .replace(/\s*~\s*/g, '~')         // Clean around ~
            .trim();
    }

    /**
     * Escape CSS content for JavaScript string
     */
    private escapeForJavaScript(content: string): string {
        return content
            .replace(/'/g, "\\'")          // Escape single quotes
            .replace(/\r?\n/g, ' ')        // Remove line breaks
            .replace(/\s+/g, ' ')          // Collapse whitespace
            .trim();
    }
}